/**
 * <media-tint src="…16px thumbnail…" hidden></media-tint>
 *
 * Colours its parent's `--media-tint` with the average of the image's outer
 * edge pixels, so media fitted inside a box of another shape sits on a fill
 * that continues its own edge. Same sampling as the media carousel's
 * "Match each slide's colours" (assets/media-carousel.js).
 *
 * A custom element rather than a page-load scan so cards added later -
 * infinite scroll, section re-renders, morphs - tint themselves on connect.
 * An edge that is fully transparent, or an image that cannot be read,
 * leaves the parent's fallback colour.
 */

/** @type {Map<string, Promise<string | null>>} */
const cache = new Map();

/**
 * @param {string} src
 * @returns {Promise<string | null>}
 */
function edgeColour(src) {
  let colour = cache.get(src);
  if (colour) return colour;

  colour = new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onerror = () => resolve(null);
    img.onload = () => {
      const size = 16;
      const canvas = document.createElement('canvas');
      canvas.width = canvas.height = size;
      const context = canvas.getContext('2d', { willReadFrequently: true });
      if (!context) return resolve(null);
      context.drawImage(img, 0, 0, size, size);
      let pixels;
      try {
        pixels = context.getImageData(0, 0, size, size).data;
      } catch {
        return resolve(null); // cross-origin without CORS
      }

      let r = 0;
      let g = 0;
      let b = 0;
      let n = 0;
      for (let y = 0; y < size; y++) {
        for (let x = 0; x < size; x++) {
          // The outer two-pixel ring: the colours that meet the empty space.
          if (x > 1 && x < size - 2 && y > 1 && y < size - 2) continue;
          const at = (y * size + x) * 4;
          const alpha = pixels[at + 3] / 255;
          if (!alpha) continue;
          r += pixels[at] * alpha;
          g += pixels[at + 1] * alpha;
          b += pixels[at + 2] * alpha;
          n += alpha;
        }
      }
      resolve(n ? `rgb(${Math.round(r / n)} ${Math.round(g / n)} ${Math.round(b / n)})` : null);
    };
    img.src = src;
  });
  cache.set(src, colour);
  return colour;
}

class MediaTint extends HTMLElement {
  static observedAttributes = ['src'];

  connectedCallback() {
    this.#apply();
  }

  attributeChangedCallback() {
    if (this.isConnected) this.#apply();
  }

  async #apply() {
    const src = this.getAttribute('src');
    const target = this.parentElement;
    if (!src || !target) return;
    const colour = await edgeColour(src);
    if (colour && this.getAttribute('src') === src) target.style.setProperty('--media-tint', colour);
  }
}

if (!customElements.get('media-tint')) customElements.define('media-tint', MediaTint);
